/**
 * How a Vega-Lite chart is set on this platform.
 *
 * Nothing here is feature-specific: it is the font, the inks, the axis styling
 * and the ``config`` any chart on the platform should share, so two features
 * drawing Vega charts cannot end up with two different-looking axes. A feature's
 * own palette — what its categories mean and what colour each one is — belongs
 * beside that feature, not here.
 */

import vegaConfig from './vega-config.json';

/**
 * Raleway, matching the app. The PAM board report bakes a "Raleway Lining"
 * build of the same family for its server-side rasteriser, which needs lining
 * figures a browser does not — so a figure rendered here and the same figure in
 * the generated document set in the same typeface. Inter and the generic sans
 * behind it, so a chart still sets if the web font has not landed.
 */
export const CHART_FONT = vegaConfig.font;

/** Ink for every value label, so a label is never mistaken for a category. */
export const LABEL_INK = vegaConfig.title.color;
/** Reversed out of a mark dark enough to need it. */
export const REVERSED_LABEL_INK = '#ffffff';
/** Axis rules, gridlines and the ink axis labels are set in. */
export const AXIS_INK = vegaConfig.axis.labelColor;
export const GRID_INK = vegaConfig.axis.gridColor;

/** Category-axis labels run long, so they get room before Vega ellipsises them. */
export const CATEGORY_LABEL_LIMIT = 280;

/**
 * The Vega-Lite version the specs are authored against, and the major the
 * transitive ``vega-lite`` is pinned to. Only ever read as a string — nothing
 * fetches it — and it is what tells ``vega-embed`` a spec is Vega-Lite rather
 * than Vega.
 */
export const VEGA_LITE_SCHEMA = 'https://vega.github.io/schema/vega-lite/v6.json';

/**
 * Shared Vega-Lite ``config``, applied by {@link VegaChart} to every spec, so a
 * chart carries no styling of its own beyond what makes it that chart. The
 * object lives in ``vega-config.json`` beside this module. The DOCX export
 * reads that same file.
 *
 * The ``legend`` block styles the charts that draw a Vega legend. A Vega-Lite
 * legend tells series apart by a colour swatch alone; a chart that cannot rest
 * identity on colour — PAM's red-to-green compliance ramp — opts out with
 * ``legend: null`` and renders an HTML key carrying the band glyph instead.
 */
export const VEGA_CONFIG = vegaConfig;
